// statusManager.ts
// Manages global status refresh for all receptors, peptides, and dockings
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getSessionReceptors, getSessionPeptides, getSessionDockings, getSessionScreenings, checkDocking } from '../backendClient';
/**
 * Refresh all statuses (receptors, peptides, dockings, screenings) that are pending or in_progress
 */
export function refreshAllStatuses() {
    return __awaiter(this, void 0, void 0, function* () {
        const refreshBtn = document.getElementById('refresh-all-status-btn');
        if (!refreshBtn) {
            console.error('Refresh all button not found');
            return;
        }
        // Disable button and show loading state
        const originalText = refreshBtn.innerHTML;
        refreshBtn.disabled = true;
        refreshBtn.innerHTML = `
    <svg class="check-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation: spin 1s linear infinite;">
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
      <path d="M21 3v5h-5"/>
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
      <path d="M3 21v-5h5"/>
    </svg>
  `;
        try {
            // Get all receptors, peptides, dockings, and screenings
            const [receptorsData, peptidesData, dockingsData, screeningsData] = yield Promise.all([
                getSessionReceptors(),
                getSessionPeptides(),
                getSessionDockings(),
                getSessionScreenings()
            ]);
            const rateExceeded = (receptorsData === null || receptorsData === void 0 ? void 0 : receptorsData.error) === 'rate_exceeded' || (peptidesData === null || peptidesData === void 0 ? void 0 : peptidesData.error) === 'rate_exceeded' ||
                (dockingsData === null || dockingsData === void 0 ? void 0 : dockingsData.error) === 'rate_exceeded' || (screeningsData === null || screeningsData === void 0 ? void 0 : screeningsData.error) === 'rate_exceeded';
            if (rateExceeded) {
                const showToast = window.showToast;
                if (showToast)
                    showToast('Too many requests. Please wait a moment before checking status again.', 'warning', 5000);
                return;
            }
            const receptors = (receptorsData === null || receptorsData === void 0 ? void 0 : receptorsData.receptors) || [];
            const peptides = (peptidesData === null || peptidesData === void 0 ? void 0 : peptidesData.peptides) || [];
            const dockings = (dockingsData === null || dockingsData === void 0 ? void 0 : dockingsData.dockings) || [];
            const screenings = (screeningsData === null || screeningsData === void 0 ? void 0 : screeningsData.screenings) || [];
            // Store original statuses to track which items were pending/in_progress before refresh
            const originalReceptorStatuses = new Map(receptors.map((r) => [r.receptorID, r.status]));
            const originalPeptideStatuses = new Map(peptides.map((p) => [p.peptideID, p.status]));
            const originalDockingStatuses = new Map(dockings.map((d) => [`${d.receptorID}-${d.peptideID}-${d.bindingSiteID}`, d.status]));
            const originalScreeningStatuses = new Map(screenings.map((s) => [s.screeningID, s.status]));
            // Filter items that need refreshing (pending, in_progress, or failed to check for updates)
            const receptorsToRefresh = receptors.filter((r) => r.status === 'pending' || r.status === 'in_progress' || r.status === 'failed' || r.status === 'job_submission_failed');
            const peptidesToRefresh = peptides.filter((p) => p.status === 'pending' || p.status === 'in_progress' || p.status === 'failed' || p.status === 'job_submission_failed');
            const dockingsToRefresh = dockings.filter((d) => d.status === 'pending' || d.status === 'in_progress' || d.status === 'failed' || d.status === 'job_submission_failed');
            const screeningsToRefresh = screenings.filter((s) => s.status === 'pending' || s.status === 'in_progress' || s.status === 'failed' || s.status === 'job_submission_failed');
            console.log(`🔄 Refreshing ${receptorsToRefresh.length} receptors, ${peptidesToRefresh.length} peptides, ${dockingsToRefresh.length} dockings, ${screeningsToRefresh.length} screenings`);
            // Mark all elements as being refreshed in bulk to prevent individual toasts
            // Include all items (pending, in_progress, and failed) to refresh them
            [...receptorsToRefresh].forEach((receptor) => {
                const element = document.querySelector(`.receptor-item[data-receptor-id="${receptor.receptorID}"]`);
                if (element) {
                    element.setAttribute('data-refreshing-all', 'true');
                }
            });
            [...peptidesToRefresh].forEach((peptide) => {
                const element = document.querySelector(`.peptide-item[data-peptide-id="${peptide.peptideID}"]`);
                if (element) {
                    element.setAttribute('data-refreshing-all', 'true');
                }
            });
            [...dockingsToRefresh].forEach((docking) => {
                const element = document.querySelector(`.docking-item[data-docking-id="${docking.dockingID}"]`);
                if (element) {
                    element.setAttribute('data-refreshing-all', 'true');
                }
            });
            [...screeningsToRefresh].forEach((screening) => {
                const element = document.querySelector(`.screening-item[data-screening-id="${screening.screeningID}"]`);
                if (element) {
                    element.setAttribute('data-refreshing-all', 'true');
                }
            });
            // Refresh receptors - click check buttons to trigger refresh (toasts will be suppressed)
            const receptorPromises = receptorsToRefresh.map((receptor) => __awaiter(this, void 0, void 0, function* () {
                try {
                    const receptorElement = document.querySelector(`.receptor-item[data-receptor-id="${receptor.receptorID}"]`);
                    if (receptorElement) {
                        const checkBtn = receptorElement.querySelector('.btn-check');
                        if (checkBtn && !checkBtn.classList.contains('btn-hidden')) {
                            checkBtn.click();
                            // Small delay to avoid overwhelming the backend
                            yield new Promise(resolve => setTimeout(resolve, 100));
                        }
                    }
                }
                catch (error) {
                    console.error(`Error refreshing receptor ${receptor.receptorID}:`, error);
                }
            }));
            // Refresh peptides - click check buttons to trigger refresh (toasts will be suppressed)
            const peptidePromises = peptidesToRefresh.map((peptide) => __awaiter(this, void 0, void 0, function* () {
                try {
                    const peptideElement = document.querySelector(`.peptide-item[data-peptide-id="${peptide.peptideID}"]`);
                    if (peptideElement) {
                        const checkBtn = peptideElement.querySelector('.btn-check');
                        if (checkBtn && !checkBtn.classList.contains('btn-hidden')) {
                            checkBtn.click();
                            // Small delay to avoid overwhelming the backend
                            yield new Promise(resolve => setTimeout(resolve, 100));
                        }
                    }
                }
                catch (error) {
                    console.error(`Error refreshing peptide ${peptide.peptideID}:`, error);
                }
            }));
            // Refresh dockings - click check buttons to trigger refresh (toasts will be suppressed)
            const dockingPromises = dockingsToRefresh.map((docking) => __awaiter(this, void 0, void 0, function* () {
                try {
                    const dockingElement = document.querySelector(`.docking-item[data-docking-id="${docking.dockingID}"]`);
                    if (dockingElement) {
                        const checkBtn = dockingElement.querySelector('.btn-check');
                        if (checkBtn && !checkBtn.classList.contains('btn-hidden')) {
                            checkBtn.click();
                            // Small delay to avoid overwhelming the backend
                            yield new Promise(resolve => setTimeout(resolve, 100));
                        }
                    }
                }
                catch (error) {
                    console.error(`Error refreshing docking ${docking.dockingID}:`, error);
                }
            }));
            // Refresh screenings - click check buttons to trigger refresh (toasts will be suppressed)
            const screeningPromises = screeningsToRefresh.map((screening) => __awaiter(this, void 0, void 0, function* () {
                try {
                    const screeningElement = document.querySelector(`.screening-item[data-screening-id="${screening.screeningID}"]`);
                    if (screeningElement) {
                        const checkBtn = screeningElement.querySelector('.btn-check');
                        if (checkBtn && !checkBtn.classList.contains('btn-hidden')) {
                            checkBtn.click();
                            // Small delay to avoid overwhelming the backend
                            yield new Promise(resolve => setTimeout(resolve, 100));
                        }
                    }
                }
                catch (error) {
                    console.error(`Error refreshing screening ${screening.screeningID}:`, error);
                }
            }));
            // Wait for all refreshes to complete (with a timeout to avoid hanging)
            yield Promise.allSettled([...receptorPromises, ...peptidePromises, ...dockingPromises, ...screeningPromises]);
            console.log('✅ All status checks triggered');
            // Wait a bit for backend responses, then reload session data to update UI
            setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                try {
                    // DON'T reload receptors and peptides completely - they are already updated by the check buttons
                    // Reloading would recreate all elements and potentially overwrite updated statuses with stale backend data
                    // For dockings, we don't reload completely to preserve progress bars
                    // Instead, we'll update existing dockings and refresh their status
                    const { getDockingList } = yield import('./dockingForm');
                    const existingDockingList = getDockingList();
                    // First, update existing dockings with latest data from backend
                    const dockingsResult = yield getSessionDockings();
                    const backendDockings = (dockingsResult === null || dockingsResult === void 0 ? void 0 : dockingsResult.dockings) || [];
                    // Update existing dockings without recreating them
                    // For completed/failed dockings, update with backend data ONLY if status changed
                    // For in_progress/pending dockings, we'll update them separately with checkDocking to get statusMessage
                    // IMPORTANT: Don't update in_progress/pending dockings here to preserve their statusMessage
                    const dockingUpdatePromises = existingDockingList
                        .filter((d) => d.status !== 'in_progress' && d.status !== 'pending')
                        .map((dockingItem) => __awaiter(this, void 0, void 0, function* () {
                        const backendDocking = backendDockings.find((d) => d.receptorID === dockingItem.receptorID &&
                            d.peptideID === dockingItem.peptideID &&
                            d.bindingSiteID === dockingItem.bindingSiteID);
                        if (backendDocking) {
                            const backendStatus = backendDocking.status || dockingItem.status;
                            // Only update if status actually changed (don't overwrite with stale data)
                            if (backendStatus !== dockingItem.status) {
                                dockingItem.status = backendStatus;
                                dockingItem.backendDockingID = backendDocking.dockingID;
                                if (backendDocking.poses && Array.isArray(backendDocking.poses)) {
                                    dockingItem.poses = backendDocking.poses;
                                }
                                // Update UI element - we'll use reloadDockingData which calls updateDockingElement
                                const { reloadDockingData } = yield import('./dockingForm');
                                yield reloadDockingData(dockingItem);
                            }
                            else {
                                // Status hasn't changed, just update poses if available
                                if (backendDocking.poses && Array.isArray(backendDocking.poses)) {
                                    dockingItem.poses = backendDocking.poses;
                                }
                                dockingItem.backendDockingID = backendDocking.dockingID;
                                // Update UI to reflect any pose changes
                                const { updateDockingElement } = yield import('./dockingForm');
                                updateDockingElement(dockingItem);
                            }
                        }
                    }));
                    // For in_progress/pending dockings, preserve their statusMessage and element
                    // We'll update them separately below with checkDocking
                    const inProgressDockings = existingDockingList.filter((d) => (d.status === 'in_progress' || d.status === 'pending') && d.element);
                    // Don't reload receptors and peptides completely - they will be updated by the check buttons
                    // Only update dockings that are completed/failed
                    yield Promise.all([
                        ...dockingUpdatePromises
                    ]);
                    // For dockings that are in_progress or pending, refresh status to get statusMessage
                    // We'll call checkDocking directly to get the statusMessage and update the UI
                    // Use the preserved list to ensure we have the correct elements
                    const dockingRefreshPromises = inProgressDockings.map((dockingItem) => __awaiter(this, void 0, void 0, function* () {
                        try {
                            // First update basic info from backend
                            const backendDocking = backendDockings.find((d) => d.receptorID === dockingItem.receptorID &&
                                d.peptideID === dockingItem.peptideID &&
                                d.bindingSiteID === dockingItem.bindingSiteID);
                            if (backendDocking) {
                                dockingItem.backendDockingID = backendDocking.dockingID;
                                if (backendDocking.poses && Array.isArray(backendDocking.poses)) {
                                    dockingItem.poses = backendDocking.poses;
                                }
                            }
                            // Call checkDocking directly to get statusMessage
                            const result = yield checkDocking(dockingItem.receptorID, dockingItem.peptideID, dockingItem.bindingSiteID, dockingItem.customBindingSiteId);
                            console.log(`[DEBUG] checkDocking result for ${dockingItem.dockingID}:`, result);
                            // Update statusMessage and status exactly like refreshDockingStatus does
                            const statusMessage = result.statusMessage || '';
                            // Determine final status
                            let dockingStatus = result.dockingStatus || dockingItem.status;
                            // Check if statusMessage indicates an error (same logic as refreshDockingStatus)
                            const errorIndicators = ['error', 'failed', 'failure', 'exception', 'killed', 'terminated'];
                            const hasErrorMessage = statusMessage && errorIndicators.some(indicator => statusMessage.toLowerCase().includes(indicator));
                            if (hasErrorMessage && (dockingStatus === 'in_progress' || dockingStatus === 'pending')) {
                                if (statusMessage.toLowerCase().includes('job submission') || statusMessage.toLowerCase().includes('submission failed')) {
                                    dockingStatus = 'job_submission_failed';
                                }
                                else {
                                    dockingStatus = 'failed';
                                }
                            }
                            // IMPORTANT: Update statusMessage in the dockingItem from the list
                            // updateDockingStatus retrieves the item from dockingList, so we need to update it there
                            const { getDockingList, updateDockingStatus, updateDockingElement } = yield import('./dockingForm');
                            const currentDockingList = getDockingList();
                            // Find the item in the list (it should be the same reference, but let's be safe)
                            const listItem = currentDockingList.find(item => item.receptorID === dockingItem.receptorID &&
                                item.peptideID === dockingItem.peptideID &&
                                item.bindingSiteID === dockingItem.bindingSiteID);
                            if (listItem) {
                                // Update statusMessage in the list item BEFORE calling updateDockingStatus
                                listItem.statusMessage = statusMessage;
                                console.log(`[DEBUG] Set statusMessage in list item for ${listItem.dockingID}: "${statusMessage}"`);
                                // Determine final status
                                let finalStatus = dockingStatus;
                                // Check if statusMessage indicates an error (same logic as refreshDockingStatus)
                                const errorIndicators = ['error', 'failed', 'failure', 'exception', 'killed', 'terminated'];
                                const hasErrorMessage = statusMessage && errorIndicators.some(indicator => statusMessage.toLowerCase().includes(indicator));
                                if (hasErrorMessage && (finalStatus === 'in_progress' || finalStatus === 'pending')) {
                                    if (statusMessage.toLowerCase().includes('job submission') || statusMessage.toLowerCase().includes('submission failed')) {
                                        finalStatus = 'job_submission_failed';
                                    }
                                    else {
                                        finalStatus = 'failed';
                                    }
                                }
                                // Call updateDockingStatus which will call updateDockingElement and updateDockingProgressBar
                                updateDockingStatus(listItem.dockingID, finalStatus);
                                // Force update again to ensure progress bar and status message are visible
                                // This is necessary because sometimes the element needs to be updated after status change
                                if (listItem.statusMessage && listItem.element) {
                                    // Ensure statusMessage is still set (it might have been lost)
                                    listItem.statusMessage = statusMessage;
                                    updateDockingElement(listItem);
                                    // Also manually ensure status message element is updated
                                    const statusMessageEl = listItem.element.querySelector(`#status-message-${listItem.dockingID}`);
                                    if (statusMessageEl) {
                                        const msg = finalStatus === 'completed' ? '' : statusMessage;
                                        statusMessageEl.textContent = msg;
                                        statusMessageEl.style.display = msg ? 'block' : 'none';
                                        if (msg) {
                                            statusMessageEl.style.fontSize = '10px';
                                            statusMessageEl.style.lineHeight = '1.2';
                                        }
                                        console.log(`[DEBUG] Manually updated status message element for ${listItem.dockingID}: "${msg}"`);
                                    }
                                    else {
                                        console.log(`[DEBUG] Status message element not found for ${listItem.dockingID}`);
                                    }
                                    console.log(`[DEBUG] Force updated docking element for ${listItem.dockingID}`);
                                }
                                console.log(`[DEBUG] Updated docking ${listItem.dockingID} with status: ${finalStatus}, message: "${statusMessage}"`);
                            }
                            else {
                                console.error(`[DEBUG] Could not find docking item in list for ${dockingItem.dockingID}`);
                            }
                        }
                        catch (error) {
                            console.error(`Error refreshing docking ${dockingItem.dockingID} statusMessage:`, error);
                        }
                    }));
                    yield Promise.allSettled(dockingRefreshPromises);
                    // Remove the data-refreshing-all attributes
                    document.querySelectorAll('[data-refreshing-all]').forEach(el => {
                        el.removeAttribute('data-refreshing-all');
                    });
                    // Reload data again to get final status after all refreshes (including screenings)
                    const [finalReceptorsData, finalPeptidesData, finalDockingsData, finalScreeningsData] = yield Promise.all([
                        getSessionReceptors(),
                        getSessionPeptides(),
                        getSessionDockings(),
                        getSessionScreenings() // all screenings (no receptor filter)
                    ]);
                    const finalReceptors = (finalReceptorsData === null || finalReceptorsData === void 0 ? void 0 : finalReceptorsData.receptors) || [];
                    const finalPeptides = (finalPeptidesData === null || finalPeptidesData === void 0 ? void 0 : finalPeptidesData.peptides) || [];
                    const finalDockings = (finalDockingsData === null || finalDockingsData === void 0 ? void 0 : finalDockingsData.dockings) || [];
                    const finalScreenings = (finalScreeningsData === null || finalScreeningsData === void 0 ? void 0 : finalScreeningsData.screenings) || [];
                    // Count final status for all items that were refreshed
                    const refreshedReceptorIDs = receptorsToRefresh
                        .filter((r) => {
                        const originalStatus = originalReceptorStatuses.get(r.receptorID);
                        return originalStatus === 'pending' || originalStatus === 'in_progress';
                    })
                        .map((r) => r.receptorID);
                    const refreshedPeptideIDs = peptidesToRefresh
                        .filter((p) => {
                        const originalStatus = originalPeptideStatuses.get(p.peptideID);
                        return originalStatus === 'pending' || originalStatus === 'in_progress';
                    })
                        .map((p) => p.peptideID);
                    const refreshedDockingKeys = dockingsToRefresh
                        .filter((d) => {
                        const key = `${d.receptorID}-${d.peptideID}-${d.bindingSiteID}`;
                        const originalStatus = originalDockingStatuses.get(key);
                        return originalStatus === 'pending' || originalStatus === 'in_progress';
                    })
                        .map((d) => `${d.receptorID}-${d.peptideID}-${d.bindingSiteID}`);
                    const refreshedScreeningIDs = screeningsToRefresh
                        .filter((s) => {
                        const originalStatus = originalScreeningStatuses.get(s.screeningID);
                        return originalStatus === 'pending' || originalStatus === 'in_progress';
                    })
                        .map((s) => s.screeningID);
                    // Do NOT re-render screening lists (no refreshScreeningListForReceptor) so the tab doesn't reload.
                    // Screening items are already updated in place by the check button clicks above.
                    // Count final status for refreshed items
                    const completedReceptors = finalReceptors.filter((r) => refreshedReceptorIDs.includes(r.receptorID) && r.status === 'completed');
                    const completedPeptides = finalPeptides.filter((p) => refreshedPeptideIDs.includes(p.peptideID) && p.status === 'completed');
                    const completedDockings = finalDockings.filter((d) => refreshedDockingKeys.includes(`${d.receptorID}-${d.peptideID}-${d.bindingSiteID}`) && d.status === 'completed');
                    const completedScreenings = finalScreenings.filter((s) => refreshedScreeningIDs.includes(s.screeningID) && s.status === 'completed');
                    const failedReceptors = finalReceptors.filter((r) => refreshedReceptorIDs.includes(r.receptorID) && (r.status === 'failed' || r.status === 'job_submission_failed'));
                    const failedPeptides = finalPeptides.filter((p) => refreshedPeptideIDs.includes(p.peptideID) && (p.status === 'failed' || p.status === 'job_submission_failed'));
                    const failedDockings = finalDockings.filter((d) => refreshedDockingKeys.includes(`${d.receptorID}-${d.peptideID}-${d.bindingSiteID}`) && (d.status === 'failed' || d.status === 'job_submission_failed'));
                    const failedScreenings = finalScreenings.filter((s) => refreshedScreeningIDs.includes(s.screeningID) && (s.status === 'failed' || s.status === 'job_submission_failed'));
                    const processingReceptors = finalReceptors.filter((r) => refreshedReceptorIDs.includes(r.receptorID) && (r.status === 'in_progress' || r.status === 'pending'));
                    const processingPeptides = finalPeptides.filter((p) => refreshedPeptideIDs.includes(p.peptideID) && (p.status === 'in_progress' || p.status === 'pending'));
                    const processingDockings = finalDockings.filter((d) => refreshedDockingKeys.includes(`${d.receptorID}-${d.peptideID}-${d.bindingSiteID}`) && (d.status === 'in_progress' || d.status === 'pending'));
                    const processingScreenings = finalScreenings.filter((s) => refreshedScreeningIDs.includes(s.screeningID) && (s.status === 'in_progress' || s.status === 'pending'));
                    // Show comprehensive toast (including screenings)
                    const totalRefreshed = refreshedReceptorIDs.length + refreshedPeptideIDs.length + refreshedDockingKeys.length + refreshedScreeningIDs.length;
                    if (totalRefreshed > 0) {
                        const showToast = window.showToast;
                        if (showToast) {
                            const messageParts = [];
                            // Completed items
                            const completedParts = [];
                            if (completedReceptors.length > 0)
                                completedParts.push(`${completedReceptors.length} receptor${completedReceptors.length > 1 ? 's' : ''}`);
                            if (completedPeptides.length > 0)
                                completedParts.push(`${completedPeptides.length} peptide${completedPeptides.length > 1 ? 's' : ''}`);
                            if (completedDockings.length > 0)
                                completedParts.push(`${completedDockings.length} docking${completedDockings.length > 1 ? 's' : ''}`);
                            if (completedScreenings.length > 0)
                                completedParts.push(`${completedScreenings.length} screening${completedScreenings.length > 1 ? 's' : ''}`);
                            if (completedParts.length > 0) {
                                messageParts.push(`${completedParts.join(', ')} completed`);
                            }
                            // Failed items
                            const failedParts = [];
                            if (failedReceptors.length > 0)
                                failedParts.push(`${failedReceptors.length} receptor${failedReceptors.length > 1 ? 's' : ''}`);
                            if (failedPeptides.length > 0)
                                failedParts.push(`${failedPeptides.length} peptide${failedPeptides.length > 1 ? 's' : ''}`);
                            if (failedDockings.length > 0)
                                failedParts.push(`${failedDockings.length} docking${failedDockings.length > 1 ? 's' : ''}`);
                            if (failedScreenings.length > 0)
                                failedParts.push(`${failedScreenings.length} screening${failedScreenings.length > 1 ? 's' : ''}`);
                            if (failedParts.length > 0) {
                                messageParts.push(`${failedParts.join(', ')} failed`);
                            }
                            // Still processing items
                            const processingParts = [];
                            if (processingReceptors.length > 0)
                                processingParts.push(`${processingReceptors.length} receptor${processingReceptors.length > 1 ? 's' : ''}`);
                            if (processingPeptides.length > 0)
                                processingParts.push(`${processingPeptides.length} peptide${processingPeptides.length > 1 ? 's' : ''}`);
                            if (processingDockings.length > 0)
                                processingParts.push(`${processingDockings.length} docking${processingDockings.length > 1 ? 's' : ''}`);
                            if (processingScreenings.length > 0)
                                processingParts.push(`${processingScreenings.length} screening${processingScreenings.length > 1 ? 's' : ''}`);
                            if (processingParts.length > 0) {
                                messageParts.push(`${processingParts.join(', ')} still processing`);
                            }
                            const message = messageParts.join(', ');
                            // Determine toast type: warning if mixed results, error if only failures, success if only completions, info if only processing
                            let toastType = 'info';
                            if (failedParts.length > 0 && completedParts.length > 0) {
                                toastType = 'warning'; // Mixed results: some completed, some failed
                            }
                            else if (failedParts.length > 0) {
                                toastType = 'error'; // Only failures
                            }
                            else if (completedParts.length > 0) {
                                toastType = 'success'; // Only completions
                            }
                            else {
                                toastType = 'info'; // Only processing
                            }
                            showToast(`Status refresh: ${message}.`, toastType, 6000);
                            // Restore button state AFTER toast is shown
                            refreshBtn.disabled = false;
                            refreshBtn.innerHTML = originalText;
                        }
                    }
                    else {
                        // No items to refresh, restore button immediately
                        refreshBtn.disabled = false;
                        refreshBtn.innerHTML = originalText;
                    }
                }
                catch (error) {
                    console.error('Error reloading session data:', error);
                    // Restore button on error
                    refreshBtn.disabled = false;
                    refreshBtn.innerHTML = originalText;
                    // Fallback to window functions if import fails
                    if (typeof window.loadSessionReceptors === 'function') {
                        yield window.loadSessionReceptors();
                    }
                    if (typeof window.loadSessionPeptides === 'function') {
                        yield window.loadSessionPeptides();
                    }
                    if (typeof window.loadSessionDockings === 'function') {
                        yield window.loadSessionDockings();
                    }
                    // Remove attributes even on error
                    document.querySelectorAll('[data-refreshing-all]').forEach(el => {
                        el.removeAttribute('data-refreshing-all');
                    });
                }
            }), 1000);
        }
        catch (error) {
            console.error('Error refreshing all statuses:', error);
            // Restore button on error
            refreshBtn.disabled = false;
            refreshBtn.innerHTML = originalText;
        }
        // Note: Button is restored in the setTimeout callback after toast is shown
    });
}
/**
 * Initialize the refresh all statuses button
 */
export function initRefreshAllStatusesButton() {
    const refreshBtn = document.getElementById('refresh-all-status-btn');
    if (!refreshBtn) {
        console.error('Refresh all statuses button not found');
        return;
    }
    refreshBtn.addEventListener('click', () => __awaiter(this, void 0, void 0, function* () {
        yield refreshAllStatuses();
    }));
}
